import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from 'swiper';
import { toast } from "sonner";
import { IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner';
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { unlockProductSchema } from "@/store/wallet/wallet.model";
import { InputForm } from "../ui/Input/InputForm";
import { Button } from "../ui/Button/Button";
import { Modal } from "../Modals/Modal.component";
import { useUnlockGift, useUnlockProduct, useUnlockResale } from "@/store/wallet/wallet.service";
import { Pages } from "@/utils/routes.utils";

import NfcBigIcon from "@/assets/svgs/nfc-big.svg?react";
import CheckIcon from "@/assets/svgs/check.svg?react";
import { WalletStore } from "@/store/wallet/wallet.store";
import { Product } from "@/store/wallet/product.model";
import { useBrandConfig } from "@/utils/useBrandConfig";

type FormProps = {
  reference: string,
  productId: string,
  unlock: (value: string) => void;
};

export const UnlockStepper = ({ reference, productId, unlock }: FormProps) => {
  const swiperRef = useRef<SwiperType | null>(null);
  const { digitalWalletConfig: { technology } } = useBrandConfig();
  const [tech, setTech] = useState<ReactNode>();

  const techs = useMemo(() => [
    <Nfc />,
    <Qr
      reference={reference}
      productId={productId}
    />,
    <Uid
      reference={reference}
      productId={productId}
      unlock={unlock}
    />,
  ], [reference, productId, unlock]);

  useEffect(() => {
    if (technology === 'NFC') {
      setTech(techs[0]);
    } else if (technology === 'QR') {
      setTech(techs[1]);
    } else if (technology === 'UID') {
      setTech(techs[2]);
    } else {
      setTech(techs[0]);
    }
  }, [technology, techs]);

  return (
    <Swiper
      spaceBetween={0}
      allowTouchMove={false}
      slidesPerView={1}
      onSwiper={(swiper) => (swiperRef.current = swiper)}
      style={{ width: '100%', height: '100%', padding: '1px', overflowY: 'auto' }}
      speed={300}
      effect="slide"
    >
      <SwiperSlide>
        <div className="flex w-full flex-col gap-y-4">
          <div className="flex-1 h-full">
            {tech}
          </div>
        </div>
      </SwiperSlide>
    </Swiper>
  )
}



const Nfc = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col px-4 py-8 justify-between items-center bg-[#F7F7F7] rounded-sm w-full aspect-square">
      <p className="text-bodySmall text-[#8B8B8B]">{t('unlock.nfcInfo')}</p>
      <NfcBigIcon />
      <div></div>
    </div>
  )
}

const Qr = ({ reference, productId }: { reference: string, productId: string }) => {
  const { t } = useTranslation();
  const walletStore = WalletStore();
  const [product, setProduct] = useState<Product>();
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const item = walletStore.products.find(el => el._id === productId) as Product;
    if (item) {
      setProduct(item);
    }
  }, [productId, walletStore]);

  const handleUnlock = (scanned: IDetectedBarcode[]) => {
    setLoading(true);

    let value = scanned[0].rawValue;
    const scannedUid = value.match(/UID=(.*)/);
    if (scannedUid && scannedUid[1] && scannedUid[1].length > 0) {
      value = scannedUid[1];
    }

    if (reference === value) {
      if (product?.gift && !product.gift.seen) {
        unlockGift(product.gift._id);
      } else if (product?.resale && !product.resale.seen) {
        unlockResale(product.resale._id);
      } else {
        unlockProduct(productId);
      }
    } else {
      setLoading(false);
      toast.error(t('unlock.failure'));
    }
  }

  const { mutate: unlockProduct } = useUnlockProduct({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const { mutate: unlockGift } = useUnlockGift({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const { mutate: unlockResale } = useUnlockResale({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex flex-col px-4 py-8 justify-between items-center bg-[#F7F7F7] rounded-sm w-full aspect-square">
        <Scanner
          paused={loading}
          components={{ finder: false }}
          sound={false}
          onScan={handleUnlock}
          formats={['qr_code', 'rm_qr_code', 'micro_qr_code']}
          children={
            loading && <div className="w-full h-full flex justify-center items-center">
              <ClipLoader />
            </div>
          }
        />
      </div>
      <p className="text-bodySmall text-[#8B8B8B]">{t('unlock.qrInfo')}</p>

      <Modal
        title={t("unlock.modal.title")}
        subtitle={t("unlock.modal.subtitle")}
        open={successModalOpen}
        closeBtn
        onClose={() => {
          navigate(Pages.HOME);
        }}
        header={<CheckIcon />}
      />
    </div>
  )
}

const Uid = ({ reference, productId }: FormProps) => {
  const { t } = useTranslation();
  const walletStore = WalletStore();

  const [product, setProduct] = useState<Product>();
  const [loading, setLoading] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const item = walletStore.products.find(el => el._id === productId) as Product;
    if (item) {
      setProduct(item);
    }
  }, [productId, setProduct, walletStore]);

  const { mutate: unlockProduct } = useUnlockProduct({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const { mutate: unlockGift } = useUnlockGift({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const { mutate: unlockResale } = useUnlockResale({
    onSuccess: () => {
      setLoading(false);
      setSuccessModalOpen(true);
    },
    onError: (message) => {
      setLoading(false);
      toast.error(message);
    },
  });

  const {
    formState: { errors, isValid },
    setValue,
    watch,
    trigger,
  } = useForm({
    resolver: zodResolver(unlockProductSchema()),
    mode: "onChange",
    defaultValues: {
      uid: '',
    }
  });

  const formValues = watch();

  useEffect(() => {
    if (formValues.uid) {
      trigger('uid');
    }
  }, [formValues.uid, trigger])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    trigger();

    if (isValid) {
      if (reference === formValues.uid) {
        setLoading(true);
        if (!product?.gift?.seen) {
          unlockGift(product!.gift!._id);
        } else if (!product?.resale!.seen) {
          unlockResale(product!.resale!._id);
        } else {
          unlockProduct(productId);
        }
      } else {
        toast.error(t('unlock.failure'));
      }
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col justify-between items-start h-[calc(100dvh-300px)]">
      <InputForm
        label={t('inputs.uid.label')}
        placeholder={t('inputs.uid.placeholder')}
        value={formValues.uid}
        handleChange={(value) => setValue('uid', value)}
        error={errors.uid?.message}
      />
      <div className="flex flex-row justify-center w-full">
        <Button
          type="submit"
          loading={loading}
          primary
          label={t('unlock.unlock')}
          onClick={() => { }}
          size="mobile"
        />
      </div>
      <Modal
        title={t("unlock.modal.title")}
        subtitle={t("unlock.modal.subtitle")}
        open={successModalOpen}
        closeBtn
        onClose={() => {
          navigate(Pages.HOME);
        }}
        header={<CheckIcon />}
      />
    </form>
  )
}